import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { Star, ShieldCheck, Droplets, Truck, ArrowDown, ShoppingBag, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { IMAGES, PRODUCT, fmt, SAVE_PCT, COMBOS, comboLineTotal, codLineTotal, COD_FEE, prepaidNet } from "../constants/site";
import CountdownTimer from "./CountdownTimer";

const HEADLINE = [
  [{ t: "Multipurpose", accent: true }],
  [{ t: "Oil Dispenser Bottle", accent: false }],
  [{ t: "for Your Kitchen", accent: true }],
];

// Scrolling offer strip — prepaid is the cheap way to buy (prepaidNet(349) = ₹332)
const PREPAID_MARQUEE = ["Pay Online ₹349", "+ Extra 5% off", "Only ₹332 on Prepaid", "Limited time offer"];

const TRUST = [
  { icon: ShieldCheck, label: "Food-Grade Glass" },
  { icon: Droplets, label: "100% Leak-Proof" },
  { icon: Truck, label: "Free Shipping" },
];

export default function Hero() {
  const { add } = useCart();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [comboQty, setComboQty] = useState(1);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], ["7deg", "-7deg"]), {
    stiffness: 120,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], ["-9deg", "9deg"]), {
    stiffness: 120,
    damping: 16,
  });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const addToCart = () => {
    add(comboQty);
    toast.success(
      comboQty > 1
        ? `${comboQty} bottles added — combo price applied, free shipping`
        : "Added to cart — free shipping applied"
    );
  };

  const buyNow = () => {
    add(comboQty);
    navigate("/checkout");
  };

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-oat-100"
      data-testid="hero-section"
    >
      {/* soft sage glow (hidden on phones — heavy blur costs scroll speed) */}
      <div className="pointer-events-none absolute -left-40 top-24 hidden h-[480px] w-[480px] rounded-full bg-sage-300/30 blur-[140px] md:block" />
      <div className="pointer-events-none absolute -right-32 bottom-0 hidden h-[420px] w-[420px] rounded-full bg-gold/20 blur-[140px] md:block" />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 pt-32 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-16 lg:pt-24">
        {/* Left — type */}
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-sage-500/30 bg-sage-100 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-sage-700"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-sage-500" />
            OmniMist™ Kitchen Craft · 2-in-1 Oil Sprayer &amp; Dispenser · 470ml
          </motion.p>

          <h1 className="font-serif text-[clamp(2.5rem,5.6vw,4.6rem)] font-medium leading-[1.04] tracking-tight text-charcoal-900">
            {HEADLINE.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{
                    duration: 0.95,
                    delay: 0.25 + i * 0.13,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="block"
                  data-testid={`hero-headline-line-${i}`}
                >
                  {line.map((w, j) =>
                    w.accent ? (
                      <em key={j} className="font-serif italic text-sage-600">
                        {w.t}
                      </em>
                    ) : (
                      <span key={j}>{w.t}</span>
                    )
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-charcoal-500 md:text-lg"
            data-testid="hero-subheadline"
          >
            Master healthy cooking with less oil and no mess. One strong glass
            bottle that sprays a fine mist or pours neatly — you decide exactly
            how much oil to use.
          </motion.p>

          {/* Price + CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-end gap-x-8 gap-y-6"
          >
            <div data-testid="hero-price-block">
              <div className="flex items-baseline gap-3">
                <span className="font-serif text-4xl font-semibold text-charcoal-900 md:text-5xl" data-testid="hero-main-price">
                  {fmt(PRODUCT.price + COD_FEE)}
                </span>
                <span className="text-xl font-semibold text-charcoal-800 line-through decoration-[2.5px] decoration-red-400/80">
                  {fmt(PRODUCT.compareAt)}
                </span>
                <span className="rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#8a6a2f]">
                  Save {Math.round(((PRODUCT.compareAt - PRODUCT.price - COD_FEE) / PRODUCT.compareAt) * 100)}%
                </span>
              </div>
              <CountdownTimer />
              {/* COD vs Prepaid price strip — prepaid is cheaper on purpose */}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="hero-pay-strip">
                <span className="flex items-center gap-1 rounded-full bg-gold/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#8a6a2f] animate-pulse">
                  <Zap className="h-3 w-3" /> Limited time
                </span>
                <span className="text-sm text-charcoal-700">
                  <strong className="font-semibold text-sage-700" data-testid="hero-prepaid-price">
                    Pay Online {fmt(PRODUCT.price)}
                  </strong>{" "}
                  — extra 5% off at checkout, just {fmt(prepaidNet(PRODUCT.price))}
                  <span className="text-charcoal-500"> · </span>
                  <strong className="font-semibold text-charcoal-900" data-testid="hero-cod-price">
                    COD {fmt(PRODUCT.price + COD_FEE)}
                  </strong>
                </span>
              </div>
              <p className="mt-2 flex items-center gap-2 text-sm text-charcoal-500">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sage-500 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-sage-500" />
                </span>
                In stock · ships free within 24 hours
              </p>

              {/* Prepaid offer marquee — scrolls right under the COD price */}
              <div
                className="marquee mt-4 overflow-hidden rounded-full border border-sage-600/30 bg-sage-800 py-2.5"
                data-testid="hero-prepaid-marquee"
              >
                <div className="marquee-track flex w-max items-center gap-8 whitespace-nowrap" style={{ animationDuration: "18s" }}>
                  {[...PREPAID_MARQUEE, ...PREPAID_MARQUEE].map((item, i) => (
                    <span key={i} className="flex items-center gap-8">
                      <span
                        className={`text-[11px] font-bold uppercase tracking-[0.18em] ${i % 2 === 0 ? "text-gold" : "text-oat-100/90"}`}
                      >
                        {item}
                      </span>
                      <span className="h-1 w-1 rounded-full bg-gold/70" aria-hidden="true" />
                    </span>
                  ))}
                </div>
              </div>

              {/* Combo selector — Buy 2 / Buy 3 bundle pricing */}
              <div className="mt-5 flex flex-wrap gap-2.5" data-testid="hero-combo-selector">
                {COMBOS.map((c) => (
                  <button
                    key={c.qty}
                    onClick={() => setComboQty(c.qty)}
                    data-testid={`hero-combo-${c.qty}`}
                    className={`relative rounded-2xl border px-4 py-2.5 text-left transition-all duration-200 ${
                      comboQty === c.qty
                        ? "border-sage-600 bg-sage-100 shadow-[0_10px_24px_-14px_rgba(124,152,133,0.8)]"
                        : "border-charcoal-900/15 bg-white hover:border-sage-500"
                    }`}
                  >
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-charcoal-700">
                      {c.qty} Bottle{c.qty > 1 ? "s" : ""}
                    </span>
                    <span className="block font-serif text-lg font-semibold text-charcoal-900">
                      {fmt(codLineTotal(c.qty))}
                    </span>
                    <span className="block text-[10px] font-medium text-sage-700">
                      {fmt(prepaidNet(comboLineTotal(c.qty)))} with Pay Online
                    </span>
                    {c.qty * PRODUCT.compareAt > codLineTotal(c.qty) && (
                      <span className="absolute -top-2 -right-2 rounded-full bg-gold px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-charcoal-900">
                        Save ₹{c.qty * PRODUCT.compareAt - codLineTotal(c.qty)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-charcoal-500" data-testid="hero-combo-note">
                COD {fmt(PRODUCT.price + COD_FEE)} per bottle · Pay Online saves 5% more
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={addToCart}
              data-testid="hero-add-to-cart-btn"
              className="group flex items-center gap-2.5 rounded-full border-2 border-charcoal-900 bg-oat-50 px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-charcoal-900 transition-all duration-300 hover:-translate-y-1 hover:border-sage-600 hover:bg-sage-100 hover:shadow-[0_16px_36px_-14px_rgba(124,152,133,0.7)] active:scale-95"
            >
              <ShoppingBag className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
              Add to Cart
              <span className="font-serif text-base normal-case tracking-normal text-sage-700">
                {fmt(codLineTotal(comboQty))}
              </span>
            </button>
            <button
              onClick={buyNow}
              data-testid="hero-buy-now-btn"
              className="group sheen flex items-center gap-2.5 rounded-full bg-gradient-to-r from-charcoal-900 to-sage-800 px-9 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-oat-100 shadow-[0_18px_40px_-16px_rgba(43,43,43,0.65)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-14px_rgba(124,152,133,0.8)] active:scale-95"
            >
              Buy Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-charcoal-900/10 pt-6"
            data-testid="hero-trust-badges"
          >
            <span className="flex items-center gap-2">
              <span className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                ))}
              </span>
              <span className="text-sm font-medium text-charcoal-700">
                4.9/5 · {PRODUCT.reviews} Home Chefs
              </span>
            </span>
            {TRUST.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="flex items-center gap-2 text-sm font-medium text-charcoal-500"
              >
                <Icon className="h-4 w-4 text-sage-600" />
                {label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        {/* Right — 3D product visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:col-span-5"
          style={{ perspective: 1200 }}
        >
          <motion.div
            style={{ y: imgY, rotateX, rotateY, transformStyle: "preserve-3d" }}
            onMouseMove={onTilt}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            className="relative mx-auto max-w-[420px] cursor-crosshair"
            data-testid="hero-product-card"
          >
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_50px_100px_-30px_rgba(43,43,43,0.45)] ring-1 ring-charcoal-900/10">
              <img
                src={IMAGES.hero}
                alt="OmniMist 2-in-1 Glass Oil Sprayer with sage green cap and white Multifunctional Oiler packaging box"
                className="h-full w-full object-cover"
                fetchPriority="high"
                decoding="async"
                data-testid="hero-product-image"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal-900/35 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="font-serif text-2xl text-oat-50">OmniMist™</p>
                  <p className="text-xs uppercase tracking-[0.22em] text-oat-100/70">
                    470ml · Borosilicate Glass
                  </p>
                </div>
                <span className="rounded-full bg-oat-50/90 px-3 py-1.5 text-xs font-semibold text-charcoal-900 backdrop-blur">
                  {fmt(PRODUCT.price + COD_FEE)}
                </span>
              </div>
            </div>

            {/* Floating spec chips */}
            <div className="animate-floaty absolute -left-6 top-10 hidden rounded-2xl border border-charcoal-900/10 bg-oat-50/90 px-4 py-3 shadow-lg backdrop-blur md:block">
              <p className="font-serif text-xl text-sage-700">0.2s</p>
              <p className="text-[11px] uppercase tracking-[0.16em] text-charcoal-500">
                Atomized Mist
              </p>
            </div>
            <div className="animate-floaty absolute -right-4 top-1/3 hidden rounded-2xl border border-charcoal-900/10 bg-oat-50/90 px-4 py-3 shadow-lg backdrop-blur md:block [animation-delay:1.4s]">
              <p className="font-serif text-xl text-sage-700">-75%</p>
              <p className="text-[11px] uppercase tracking-[0.16em] text-charcoal-500">
                Calories per Dish
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 lg:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-charcoal-500/60" />
      </motion.div>
    </section>
  );
}
